import { jsx as _jsx } from "react/jsx-runtime";
import { useIdentityPreferences, useRpcRouter, useSettings } from '@nest/common-core-frontend-react';
import { useDialogs } from '@nest/uikit-react';
import { useCallback, useMemo, useState } from 'react';
import { PdfPreviewDialog } from './PdfPreviewDialog';
import { PrinterSelectorDialog } from './PrinterSelectorDialog';
export function usePrintActionHandler(dialogIdentifier, printAction, options = {}) {
    const { openCustomDialog, alert } = useDialogs();
    const { updateValues, getValue } = useIdentityPreferences({ applicationName: 'desktop' });
    const { apiUrl } = useSettings();
    const printersClient = useRpcRouter(apiUrl);
    const [isLoading, setIsLoading] = useState(false);
    const defaultAllowedActions = useMemo(() => {
        return options.allowedActions ?? ['download', 'print', 'preview'];
    }, [options.allowedActions]);
    const defaultNumberOfCopies = useMemo(() => {
        return options.numberOfCopies ?? 1;
    }, [options.numberOfCopies]);
    const openPrinterSelector = useCallback(async () => {
        return new Promise(resolve => {
            const savedPrinterIds = (getValue(`printDialog.${dialogIdentifier}.printerIds`) ?? []);
            const savedNumberOfCopies = getValue(`printDialog.${dialogIdentifier}.numberOfCopies`) ?? defaultNumberOfCopies;
            openCustomDialog(PrinterSelectorDialog, {
                identifier: dialogIdentifier,
                allowedActions: defaultAllowedActions,
                numberOfCopies: savedNumberOfCopies,
                initialPrinterIds: savedPrinterIds,
                loadPrintersByIds: async (ids) => {
                    if (ids.length === 0) {
                        return [];
                    }
                    const result = await printersClient.printers.getAll.execute({
                        filter: { id: { $in: ids } }
                    });
                    return result ?? [];
                },
                loadAllPrinters: async () => {
                    const result = await printersClient.printers.getAll.execute({});
                    return result ?? [];
                },
                onPrintersChange: async (printers) => {
                    if (printers.length > 0) {
                        await updateValues({ [`printDialog.${dialogIdentifier}.printerIds`]: printers.map(p => p.id) });
                    }
                },
                onClose: () => {
                    resolve(null);
                },
                onSubmit: value => {
                    updateValues({ [`printDialog.${dialogIdentifier}.numberOfCopies`]: value.numberOfCopies });
                    resolve(value);
                }
            });
        });
    }, [dialogIdentifier, defaultAllowedActions, defaultNumberOfCopies, getValue, openCustomDialog, printersClient, updateValues]);
    const run = useCallback(async (args) => {
        const selection = await openPrinterSelector();
        if (selection == null) {
            return false;
        }
        setIsLoading(true);
        let closeLoading = null;
        const close = openCustomDialog(close => {
            closeLoading = close;
            return (_jsx("div", { className: "fixed inset-0 z-[100] flex items-center justify-center bg-black/50", children: _jsx("div", { className: "rounded-md bg-background p-6 shadow-lg", children: _jsx("div", { className: "text-base", children: "Zpracov\u00E1v\u00E1m tisk\u2026" }) }) }));
        });
        try {
            const response = await printAction(selection.numberOfCopies, selection.action == 'preview' ? [] : (selection.printerIds ?? []), args);
            if (selection.action === 'preview' && response instanceof Blob) {
                const objectUrl = URL.createObjectURL(response);
                openCustomDialog(PdfPreviewDialog, {
                    objectUrl
                });
            }
            return true;
        }
        catch (error) {
            let messages = [];
            if ('items' in error && Array.isArray(error.items)) {
                messages = error.items.map(i => i.message);
            }
            else if (typeof error === 'object' && 'message' in error) {
                messages.push(error.message);
            }
            else {
                messages.push(error.toString());
            }
            alert({
                message: messages,
                type: 'error',
                title: 'Chyba při tisku'
            });
            close();
            return false;
        }
        finally {
            if (closeLoading) {
                closeLoading();
            }
            setIsLoading(false);
        }
    }, [alert, openCustomDialog, openPrinterSelector, printAction]);
    return {
        run,
        isLoading
    };
}
//# sourceMappingURL=use-print-action-handler.hook.js.map